import { Typography, Button, Box, FormLabel } from "@mui/joy";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CustomTextField from "../../main/widgets/CustomTextField";
import { primaryColor } from "../../main/theme/constants";
import { useSiteSettings } from "../../main/context/SiteSettingsContext";
import { useAuth } from "../../main/context/AuthContext";
import { resolvePostLoginRedirect } from "../../main/utils/customerAccountApproval";
import { setAppLanguage, resolveAppLanguage } from "../../../i18n";
import { translateAggregateBackendMessage } from "../../main/utils/backendErrorTranslation";
import { getSiteDisplayName } from "../../main/utils/siteDisplayName";

export default function Login() {
  const { t, i18n } = useTranslation('ui');
  const currentLang = resolveAppLanguage(i18n.language);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const { settings } = useSiteSettings();
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();

  const welcomeBg =
    settings?.welcome_background && String(settings.welcome_background).trim();
  const siteDisplayName = getSiteDisplayName(settings, i18n.language);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowErrorMsg(false);

    const result = await login({ username, password });
    
    if (result.success) {
      const loginUser = result.data && result.data.user;
      let storedPermissions = [];
      try {
        const raw = localStorage.getItem('user_permissions');
        storedPermissions = raw ? JSON.parse(raw) : [];
      } catch (_) {
        storedPermissions = [];
      }
      const roleNames = loginUser && Array.isArray(loginUser.roles)
        ? loginUser.roles.map((role) => role.name)
        : [];
      window.location.href = resolvePostLoginRedirect(roleNames, storedPermissions, loginUser);
    } else {
      setShowErrorMsg(true);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "#f7f8fc",
      }}
    >
      {/* Left Side: Login Form */}
      <Box
        sx={{
          width: { xs: "100%", md: 500 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
          backgroundColor: "#ffffff",
          boxShadow: { xs: "none", md: "0 8px 24px rgba(0, 0, 0, 0.1)" },
          borderRadius: { xs: 0, md: "12px" },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Box
            role="group"
            aria-label={t('language.languageToggle')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: 0.25,
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              size="sm"
              variant={currentLang === 'en' ? 'solid' : 'plain'}
              color={currentLang === 'en' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('en')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.english')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'zh' ? 'solid' : 'plain'}
              color={currentLang === 'zh' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('zh')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.chinese')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'ar' ? 'solid' : 'plain'}
              color={currentLang === 'ar' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('ar')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.arabic')}
            </Button>
          </Box>
        </Box>
        <Typography
          level="h2"
          sx={{ fontWeight: "bold", marginBottom: 2, color: "#333333" }}
        >
          {siteDisplayName
            ? t('auth.welcomeBackTo', { site: siteDisplayName })
            : t('auth.welcomeBack')}
        </Typography>
        <Typography
          level="body2"
          sx={{ color: "text.secondary", marginBottom: 4, fontSize: "16px" }}
        >
          {t('auth.signInContinue')}
        </Typography>

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Box sx={{ marginBottom: 3 }}>
            <CustomTextField
              required
              type="text"
              onChange={(event) => setUsername(event.target.value)}
              value={username}
              label={t('auth.username')}
              fullWidth
              sx={{
                "& .MuiInput-root": {
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  padding: "10px",
                },
              }}
            />
          </Box>

          <Box sx={{ marginBottom: 2 }}>
            <CustomTextField
              required
              type="password"
              onChange={(event) => setPassword(event.target.value)}
              value={password}
              label={t('auth.password')}
              fullWidth
              sx={{
                "& .MuiInput-root": {
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  padding: "10px",
                },
              }}
            />
          </Box>

          {showErrorMsg && (
            <FormLabel
              sx={{
                color: "error.main",
                marginBottom: 2,
                fontSize: "14px",
              }}
            >
              {translateAggregateBackendMessage(error, t) || t('auth.loginFailed')}
            </FormLabel>
          )}

          <Button
            type="submit"
            loading={loading}
            sx={{
              width: "100%",
              padding: "12px 16px",
              backgroundColor: primaryColor,
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "16px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              borderRadius: "8px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: primaryColor,
                transform: "scale(1.02)",
              },
            }}
          >
            {t('auth.login')}
          </Button>

          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate('/customer-login')}
            sx={{
              width: "100%",
              marginTop: 2,
              padding: "12px 16px",
              fontWeight: 600,
              fontSize: "15px",
              borderRadius: "8px",
              borderColor: primaryColor,
              color: primaryColor,
              "&:hover": {
                borderColor: primaryColor,
                backgroundColor: `${primaryColor}10`,
              },
            }}
          >
            {t('auth.customerLogin')}
          </Button>
        </form>
      </Box>

      {/* Right Side: Background Image */}
      <Box
        sx={{
          flexGrow: 1,
          flexBasis: 0,
          minWidth: 0,
          minHeight: { xs: "40vh", md: "100vh" },
          backgroundImage: welcomeBg
            ? `url(${welcomeBg})`
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          },
        }}
      />
    </Box>
  );
}
